import React, { useState, useEffect } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { api } from '../lib/api';
import { StarIcon, CheckCircleIcon, XCircleIcon } from '@heroicons/react/24/outline';
import { StarIcon as StarIconSolid } from '@heroicons/react/24/solid';

interface RatingSettings {
  id: string;
  mode: 'standard' | 'strict';
  google_place_id?: string;
  google_place_name?: string;
  yelp_business_id?: string;
  facebook_page_id?: string;
  custom_feedback_email?: string;
  policy_safe_language?: string;
  thank_you_message?: string;
  branding?: {
    company_name?: string;
    logo_url?: string;
    primary_color?: string;
    remove_branding?: boolean;
  };
}

interface RatingSubmission {
  rating: number;
  feedback?: string;
  source?: string;
  campaign_id?: string;
  contact_id?: string;
}

interface RatingResponse {
  redirect_url?: string;
  destination: 'google' | 'yelp' | 'facebook' | 'private_feedback';
  message: string;
  thank_you_message?: string;
}

interface RatingPageProps {
  tenantId?: string;
  campaignId?: string;
  contactId?: string;
  source?: string;
  isPublicRoute?: boolean; // If true, always use public endpoint (for public routes like /rating-gate)
  onRatingComplete?: (response: RatingResponse) => void;
}

export const RatingPage: React.FC<RatingPageProps> = ({
  tenantId,
  campaignId,
  contactId,
  source = 'web',
  isPublicRoute = false,
  onRatingComplete
}) => {
  const [rating, setRating] = useState(0);
  const [feedback, setFeedback] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [response, setResponse] = useState<RatingResponse | null>(null);
  const [isRedirecting, setIsRedirecting] = useState(false);

  // Fetch rating settings - use public endpoint if:
  // 1. This is a public route (isPublicRoute=true), OR
  // 2. We have campaign_id or contact_id (public access)
  // Otherwise use authenticated endpoint (for internal uses)
  const usePublicEndpoint = isPublicRoute || !!(campaignId || contactId);
  const { data: settings, isLoading: settingsLoading, error: settingsError } = useQuery<RatingSettings>({
    queryKey: ['rating-settings', tenantId, campaignId, contactId, usePublicEndpoint],
    queryFn: async () => {
      if (usePublicEndpoint) {
        // Use public endpoint for customers (no auth required)
        const response = await api.ratingGate.getSettingsPublic(campaignId, contactId);
        return response.data;
      } else {
        // Use authenticated endpoint for internal use
        const response = await api.ratingGate.getSettings();
        return response.data;
      }
    },
    enabled: true, // Always enabled - let the API handle tenant contexts
    retry: 1, // Only retry once to avoid infinite loading
  });

  // Fetch tenant branding - only for authenticated users (internal use)
  // For public rating gate, branding comes from settings response
  const { data: branding } = useQuery({
    queryKey: ['branding'],
    queryFn: async () => {
      const response = await api.branding.get();
      return response.data;
    },
    enabled: !usePublicEndpoint, // Only fetch branding if not using public endpoint
    retry: 1,
  });

  // Get company name from branding or settings
  const companyName = branding?.company_name || settings?.branding?.company_name || 'Reputation Manager';
  const logoUrl = branding?.logo_url || settings?.branding?.logo_url;
  const primaryColor = branding?.primary_color || settings?.branding?.primary_color || '#667eea';

  // Submit rating mutation
  const submitRatingMutation = useMutation({
    mutationFn: async (submission: RatingSubmission) => {
      const response = await api.ratingGate.submitRating(submission);
      return response.data;
    },
    onSuccess: (data: RatingResponse) => {
      setResponse(data);
      setSubmitted(true);
      onRatingComplete?.(data);
      
      // Automatically redirect for high ratings (4-5 stars) if redirect_url is available
      // Don't auto-redirect for mailto links (let user click the button)
      if (data.redirect_url && (rating >= 4) && !data.redirect_url.startsWith('mailto:')) {
        // Small delay to show thank you message, then redirect
        setTimeout(() => {
          setIsRedirecting(true);
          window.location.href = data.redirect_url!;
        }, 2000); // 2 second delay to allow time to read the message
      }
    },
    onError: (error) => {
      console.error('Error submitting rating:', error);
      // Show error message to user
    }
  });

  const handleRatingClick = (selectedRating: number) => {
    setRating(selectedRating);
  };

  const handleSubmit = () => {
    if (rating === 0) {
      alert('Please select a rating before submitting.');
      return;
    }

    const submission: RatingSubmission = {
      rating,
      feedback: feedback.trim() || undefined,
      source,
      campaign_id: campaignId,
      contact_id: contactId
    };

    submitRatingMutation.mutate(submission);
  };

  const handleRedirect = () => {
    if (response?.redirect_url) {
      // Redirect in same window for better UXs
      window.location.href = response.redirect_url;
    }
  };

  const renderStars = () => {
    return (
      <div className="flex justify-center space-x-1 sm:space-x-2 mb-4 sm:mb-6">
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            onClick={() => handleRatingClick(star)}
            className="focus:outline-none focus-primary rounded-lg p-1"
            aria-label={`Rate ${star} star${star > 1 ? 's' : ''}`}
          >
            {star <= rating ? (
              <StarIconSolid className="w-8 h-8 sm:w-10 sm:h-10 lg:w-12 lg:h-12 text-yellow-400" />
            ) : (
              <StarIcon className="w-8 h-8 sm:w-10 sm:h-10 lg:w-12 lg:h-12 text-gray-300 hover:text-yellow-200" />
            )}
          </button>
        ))}
      </div>
    );
  };

  const renderRatingLabels = () => {
    const labels = ['Poor', 'Fair', 'Good', 'Very Good', 'Excellent'];
    return (
      <div className="flex justify-between text-xs sm:text-sm text-gray-600 mb-4 sm:mb-6 px-1">
        {labels.map((label, index) => (
          <span
            key={index}
            className={`${index + 1 <= rating ? 'text-yellow-600 font-medium' : ''} truncate max-w-[20%]`}
            title={label}
          >
            {label}
          </span>
        ))}
      </div>
    );
  };

  const renderSubmissionForm = () => (
    <div className="space-y-4 sm:space-y-6">
      <div className="text-center">
        {logoUrl && (
          <div className="mb-4 sm:mb-6">
            <img 
              src={logoUrl} 
              alt={companyName}
              className="mx-auto max-h-12 sm:max-h-16 max-w-36 sm:max-w-48"
            />
          </div>
        )}
        <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2 sm:mb-4">
          How was your experience?
        </h2>
        <p className="text-sm sm:text-base text-gray-600 mb-4 sm:mb-8 px-2">
          {settings?.policy_safe_language || 
           "We value your feedback and would appreciate you sharing your experience to help other customers make informed decisions."}
        </p>
      </div>

      {renderStars()}
      {renderRatingLabels()}

      <div>
        <label htmlFor="feedback" className="block text-sm font-medium text-gray-700 mb-2">
          Tell us more (optional)
        </label>
        <textarea
          id="feedback"
          value={feedback}
          onChange={(e) => setFeedback(e.target.value)}
          rows={4}
          className="w-full px-3 py-2 text-sm sm:text-base border border-gray-300 rounded-md shadow-sm focus-primary"
          placeholder="Share more details about your experience..."
          maxLength={1000}
        />
        <p className="text-xs text-gray-500 mt-1">
          {feedback.length}/1000 characters
        </p>
      </div>

      <button
        onClick={handleSubmit}
        disabled={rating === 0 || submitRatingMutation.isPending}
        className="w-full text-sm sm:text-base text-white py-2.5 sm:py-3 px-4 rounded-md font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-opacity"
        style={{
          backgroundColor: primaryColor
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.opacity = '0.9';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.opacity = '1';
        }}
      >
        {submitRatingMutation.isPending ? 'Submitting...' : 'Submit Rating'}
      </button>
    </div>
  );

  const renderThankYouMessage = () => (
    <div className="text-center space-y-4 sm:space-y-6">
      <div className="flex justify-center">
        {response?.destination === 'private_feedback' ? (
          <CheckCircleIcon className="w-12 h-12 sm:w-16 sm:h-16 text-green-500" />
        ) : (
          <StarIconSolid className="w-12 h-12 sm:w-16 sm:h-16 text-yellow-500" />
        )}
      </div>

      <div>
        <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2 sm:mb-4">
          Thank You!
        </h2>
        <p className="text-sm sm:text-base text-gray-600 mb-4 sm:mb-6 px-2">
          {response?.message || 'Thank you for your feedback!'}
        </p>
        
        {response?.thank_you_message && (
          <p className="text-xs sm:text-sm text-gray-500 mb-4 sm:mb-6 px-2">
            {response.thank_you_message}
          </p>
        )}
      </div>

      {response?.redirect_url && (
        <div className="space-y-3 sm:space-y-4">
          {response.redirect_url.startsWith('mailto:') ? (
            // Mailto link for private feedback email
            <div className="space-y-3 sm:space-y-4">
              <p className="text-base sm:text-lg text-gray-700 font-medium">
                Contact Us Directly
              </p>
              <p className="text-xs sm:text-sm text-gray-500 mb-4 px-2">
                Click the button below to send us an email with your feedback.
              </p>
              <a
                href={response.redirect_url}
                className="inline-block w-full text-white py-2.5 sm:py-3 px-4 sm:px-6 rounded-md font-medium hover:opacity-90 focus:outline-none focus-primary focus:ring-offset-2 text-center text-sm sm:text-base"
                style={{ backgroundColor: primaryColor }}
              >
                Send Feedback via Email
              </a>
            </div>
          ) : isRedirecting ? (
            // Auto-redirecting for public platforms
            <div className="text-center">
              <p className="text-sm sm:text-base text-gray-700 mb-2">Redirecting you to share your experience...</p>
              <div
                className="animate-spin rounded-full h-8 w-8 border-b-2 mx-auto"
                style={{ borderBottomColor: primaryColor }}
              ></div>
            </div>
          ) : (
            // Manual redirect button for public platforms
            <>
              <p className="text-base sm:text-lg text-gray-700 font-medium px-2">
                {response.destination === 'google' && 'Please help our business by sharing your experience on Google!'}
                {response.destination === 'yelp' && 'Please help our business by sharing your experience on Yelp!'}
                {response.destination === 'facebook' && 'Please help our business by sharing your experience on Facebook!'}
              </p>
              <p className="text-xs sm:text-sm text-gray-500 mb-4 px-2">
                You'll be redirected automatically in a moment, or click below to go now.
              </p>
              <button
                onClick={handleRedirect}
                className="text-white py-2.5 sm:py-3 px-4 sm:px-6 rounded-md font-medium hover:opacity-90 focus:outline-none focus-primary focus:ring-offset-2 w-full text-sm sm:text-base"
                style={{ backgroundColor: primaryColor }}
              >
                Share Your Experience on {response.destination === 'google' ? 'Google' : response.destination === 'yelp' ? 'Yelp' : 'Facebook'}
              </button>
            </>
          )}
        </div>
      )}

      <div className="pt-4 border-t border-gray-200">
        <p className="text-xs text-gray-500 px-2">
          Your feedback helps us improve and assists other customers in making informed decisions
        </p>
      </div>
    </div>
  );

  if (settingsLoading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <div
          className="animate-spin rounded-full h-8 w-8 border-b-2"
          style={{ borderBottomColor: primaryColor }}
        ></div>
      </div>
    );
  }

  if (settingsError) {
    return (
      <div className="max-w-md mx-auto bg-white rounded-lg shadow-lg p-4 sm:p-6 lg:p-8">
        <div className="text-center space-y-3 sm:space-y-4">
          <XCircleIcon className="w-12 h-12 sm:w-16 sm:h-16 text-red-500 mx-auto" />
          <h2 className="text-lg sm:text-xl font-bold text-gray-900">Unable to Load Rating Settings</h2>
          <p className="text-sm sm:text-base text-gray-600 px-2">
            There was an error loading the rating configuration. Please try refreshing the page.
          </p>
          <button
            onClick={() => window.location.reload()}
            className="text-white py-2 px-4 rounded-md font-medium hover:opacity-90 focus:outline-none focus-primary focus:ring-offset-2 text-sm sm:text-base"
            style={{ backgroundColor: primaryColor }}
          >
            Refresh Page
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-md mx-auto bg-white rounded-lg shadow-lg p-4 sm:p-6 lg:p-8">
      {submitted ? renderThankYouMessage() : renderSubmissionForm()}
    </div>
  );
};
